import {
  Fragment,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { isError } from '@backstage/errors';
import {
  IconComponent,
  PendingOAuthRequest,
  configApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useObservable from 'react-use/esm/useObservable';

const useStyles = makeStyles(
  theme => ({
    dialog: {
      paddingTop: theme.spacing(1),
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    titleHeading: {
      fontSize: theme.typography.h6.fontSize,
    },
    contentList: {
      padding: 0,
    },
    listItem: {
      paddingLeft: theme.spacing(2),
    },
    loginButton: {
      marginLeft: theme.spacing(2),
      flexShrink: 0,
    },
  }),
  { name: 'CustomOAuthRequestDialog' },
);

function renderProviderIcon(
  icon: PendingOAuthRequest['provider']['icon'],
): JSX.Element {
  if (icon === null) {
    return <Fragment />;
  }
  if (isValidElement(icon)) {
    return icon;
  }
  const Icon = icon as IconComponent;
  return <Icon fontSize="large" />;
}

function AuthRequestRow(props: {
  request: PendingOAuthRequest;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  appTitle: string;
}) {
  const { request, busy, setBusy, appTitle } = props;
  const classes = useStyles();
  const [error, setError] = useState<string>();
  const message =
    request.provider.message ??
    `Sign-in to allow ${appTitle} access to ${request.provider.title} APIs and identities.`;

  const handleContinue = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await request.trigger();
    } catch (e) {
      // Same behavior as core LoginRequestListItem: ignore popup cancel.
      if (!(isError(e) && e.name === 'PopupRejectedError')) {
        setError(isError(e) ? e.message : 'An unspecified error occurred');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ListItem disabled={busy} classes={{ root: classes.listItem }}>
      <ListItemAvatar>
        {renderProviderIcon(request.provider.icon)}
      </ListItemAvatar>
      <Box display="flex" alignItems="center" flex={1} minWidth={0}>
        <Box flex={1} minWidth={0}>
          <ListItemText
            primary={request.provider.title}
            secondaryTypographyProps={{ component: 'div' }}
            secondary={
              <Fragment>
                {message && (
                  <Typography variant="subtitle2" color="textSecondary">
                    {message}
                  </Typography>
                )}
                {error && <Typography color="error">{error}</Typography>}
              </Fragment>
            }
          />
        </Box>
        <Button
          color="primary"
          variant="contained"
          className={classes.loginButton}
          disabled={busy}
          onClick={handleContinue}
        >
          Log in
        </Button>
      </Box>
    </ListItem>
  );
}

/**
 * App override of Backstage OAuthRequestDialog: dismiss with close (x) instead
 * of "Reject All". Auth still goes through oauthRequestApi (trigger/reject).
 */
export const CustomOAuthRequestDialog = () => {
  const classes = useStyles();
  const [busy, setBusy] = useState(false);
  const oauthRequestApi = useApi(oauthRequestApiRef);
  const configApi = useApi(configApiRef);
  const appTitle = configApi.getString('app.title');

  const requests = useObservable(
    useMemo(() => oauthRequestApi.authRequest$(), [oauthRequestApi]),
    [],
  );

  const handleDismiss = useCallback(() => {
    for (const request of requests) {
      request.reject();
    }
  }, [requests]);

  return (
    <Dialog
      open={requests.length > 0}
      fullWidth
      maxWidth="xs"
      classes={{ paper: classes.dialog }}
      aria-labelledby="oauth-req-dialog-title"
      disableBackdropClick
      onClose={handleDismiss}
    >
      <DialogTitle disableTypography id="oauth-req-dialog-title">
        <Box className={classes.titleRow}>
          <Typography
            className={classes.titleHeading}
            variant="h6"
            component="span"
          >
            Login Required
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={handleDismiss}
            size="small"
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers classes={{ root: classes.contentList }}>
        <List>
          {requests.map(request => (
            <AuthRequestRow
              key={request.provider.id}
              request={request}
              busy={busy}
              setBusy={setBusy}
              appTitle={appTitle}
            />
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};
